<!-- 
  @component 
  ### InfoIcon
  An icon with a tooltip that can be used to show helpful information.
  With built-in good accessibility and styling.
-->
<script lang="ts">
  import { browser } from '$app/environment';
  import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
  import IconWrapper from '$components/mds/IconWrapper/IconWrapper.svelte';
  import { tooltip } from '$lib/directives/tooltip';
  import type { Placement } from 'tippy.js';
  import { IconWrapperSize } from '$mds/IconWrapper';

  export let node: HTMLElement | undefined = undefined;
  export let icon = faCircleInfo;
  export let size: IconWrapperSize = 'base';
  export let content = '';
  export let asSpan = false;
  export let allowHTML = false;
  export let placement: Placement = 'top';
  export let hideOnClick: boolean | 'toggle' = false;
  /** Allow the tooltip to be triggered by a different element */
  export let target: HTMLElement | HTMLElement[] | undefined = undefined;
  export let tabindex: null | number | undefined = undefined;

  /** The default color of the tooltip (uses text colors) */
  export let defaultColor = 'text-ondark-subtle';
  let className = 'relative';
  export { className as class };

  let tooltipInstance: ReturnType<typeof tooltip>;
  const tooltipCreate = () => {
    if (!node) return;
    return (tooltipInstance = tooltip(node, {
      content: ' ', // empty string so that the tooltip isnt killed before initialization
      allowHTML,
      placement,
      hideOnClick,
      triggerTarget: target
    }));
  };

  /**
   * Set the content of the tooltip, or create a new one
   * and enable/disable the tooltip if needed
   */
  const setContent = (content: string) => {
    if (!tooltipInstance) {
      tooltipCreate();
    }
    if (content) {
      tooltipInstance?.instance?.enable?.();
      tooltipInstance?.instance?.setContent?.(content);
    } else {
      tooltipInstance?.instance?.disable?.();
    }
  };

  /**
   * Toggle the visibility of the tooltip when the button is clicked
   */
  const toggleShow = () => {
    if (tooltipInstance?.instance?.state?.isShown) {
      tooltipInstance?.instance?.hide?.();
    } else {
      tooltipInstance?.instance?.show?.();
    }
  };

  /**
   * Set the content of the tooltip dynamically so
   * it will be updated when the content changes
   */
  $: if (browser && node && content) {
    setContent(content);
  }

  /**
   * Set the trigger target for the tooltip dynamically
   * so it will be updated when the target changes
   */
  $: if (browser && node && target) {
    tooltipInstance?.instance?.setProps?.({ triggerTarget: target });
  }

  const classes = {
    text:
      defaultColor +
      ' ' +
      'focus-visible:text-ondark-primary focus-within:text-ondark-primary hover:text-ondark-primary focus:text-ondark-primary group-hover:text-ondark-primary group-focus:text-ondark-primary',
    ring: 'ring-transparent focus:ring-2 focus:ring-ondark-focused hover:ring-ondark-focused',
    interaction: 'transition-all duration-300 before:absolute before:-inset-2',
    size: 'inline-flex aspect-square items-center justify-center rounded-full'
  };
</script>

<svelte:element
  this={asSpan ? 'span' : 'button'}
  role={asSpan ? undefined : 'button'}
  bind:this={node}
  class="{classes.text} {classes.interaction} {classes.size} {classes.ring} {className}"
  on:click={toggleShow}
  on:click
  on:pointerover
  on:pointerout
  {tabindex}
>
  <IconWrapper {size} {icon} />
</svelte:element>
